import React, { useRef, useState } from 'react';
import { useI18n } from './i18n';
import { isAuthenticated } from './auth';
import { ACCEPT, IMAGE_ACCEPT, isCatalogOwner, validateNewFiles } from './attachments';
import { ATTACHMENT_ROLES } from './codes';

// Chooses files to attach to a sheet. Controlled component: `items` is [{ file, role, caption }].
// The parent uploads them (uploadAttachments) once the sheet exists. Only registered users can attach files.
// Props: items, onChange(items), existingCount (attachments already on the owner, default 0),
// ownerType 'sheets' (default) | 'wines' | 'beers' | 'products': catalog files are label photos (images, no role to choose).
function AttachmentPicker({ items, onChange, existingCount = 0, ownerType = 'sheets' }) {
  const { t } = useI18n();
  const inputRef = useRef(null);
  const [error, setError] = useState(null);
  const catalog = isCatalogOwner(ownerType);

  if (!isAuthenticated()) {
    return <p className="import-hint">{t(catalog ? 'attachments.login_required_labels' : 'attachments.login_required')}</p>;
  }

  const addFiles = (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (files.length === 0) return;
    const problem = validateNewFiles(existingCount + items.length, files, ownerType);
    if (problem) { setError(problem); return; }
    setError(null);
    onChange([...items, ...files.map((file) => ({ file, role: catalog ? 'label' : (file.type === 'application/pdf' ? 'menu' : 'photo'), caption: '' }))]);
  };

  const update = (index, patch) => onChange(items.map((it, i) => (i === index ? { ...it, ...patch } : it)));
  const remove = (index) => onChange(items.filter((_, i) => i !== index));

  return (
    <div className="attachment-picker">
      <input ref={inputRef} type="file" accept={catalog ? IMAGE_ACCEPT : ACCEPT} multiple onChange={addFiles} style={{ display: 'none' }} />
      <button type="button" className="reset-btn" onClick={() => inputRef.current && inputRef.current.click()}>
        {t(catalog ? 'attachments.add_label' : 'attachments.add')}
      </button>
      <small className="import-hint"> {t(catalog ? 'attachments.hint_labels' : 'attachments.hint')}</small>
      {error && <div className="message error" style={{ marginTop: 6 }}>{t(error)}</div>}
      {items.length > 0 && (
        <ul className="attachment-pending">
          {items.map((it, i) => (
            <li key={`${it.file.name}-${i}`}>
              <span className="attachment-name">{it.file.name}</span>
              {!catalog && (
                <select value={it.role} onChange={(e) => update(i, { role: e.target.value })}>
                  {ATTACHMENT_ROLES.map((r) => <option key={r} value={r}>{t(`attachments.role.${r}`)}</option>)}
                </select>
              )}
              <input value={it.caption} placeholder={t('attachments.caption_placeholder')} maxLength={300}
                onChange={(e) => update(i, { caption: e.target.value })} />
              <button type="button" className="chip" onClick={() => remove(i)} title={t('attachments.remove')}>×</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default AttachmentPicker;
